import React from 'react';
import { Input } from './ui/input';
import { Search, X } from 'lucide-react';

/**
 * Shared filter chip + search input row for list pages.
 *
 * Mobile-first: chip row scrolls horizontally with iOS snap on phones and
 * wraps on desktop. Search input is 44 px tall with 16 px text on mobile
 * so iOS Safari doesn't auto-zoom on focus.
 *
 * Props:
 *   - chips: [{ key, label, count?, accent?, icon? }]. `accent` picks the
 *     active pill colour (`amber` | `blue` | `emerald` | `rose` | `slate`).
 *   - activeChip / onChipChange: controlled selection.
 *   - query / onQueryChange: controlled search string.
 *   - placeholder: search box placeholder.
 *   - resultCount / totalCount: shown as "N of M reports match" hint when
 *     either a chip filter (other than the first) OR a query is active.
 *   - testIdPrefix: string used for data-testid attributes so multiple bars
 *     on the same page don't clash.
 */
const ACCENT_ACTIVE = {
  amber:   'bg-amber-600 text-white border-amber-600',
  blue:    'bg-blue-600 text-white border-blue-600',
  emerald: 'bg-emerald-600 text-white border-emerald-600',
  rose:    'bg-rose-600 text-white border-rose-600',
  slate:   'bg-slate-900 text-white border-slate-900',
};

export const ListFilterBar = ({
  chips = [],
  activeChip,
  onChipChange,
  query = '',
  onQueryChange,
  placeholder = 'Search…',
  resultCount = null,
  totalCount = null,
  resultNoun = 'result',
  testIdPrefix = 'list',
}) => {
  const filterActive = activeChip && chips.length && activeChip !== chips[0].key;
  const showCount = (query && query.trim()) || filterActive;

  return (
    <>
      <div
        className="-mx-1 mb-3 flex gap-2 overflow-x-auto pb-1 pl-1 pr-1 snap-x snap-mandatory md:flex-wrap md:overflow-visible md:snap-none"
        data-testid={`${testIdPrefix}-filter-chips`}
      >
        {chips.map((c) => {
          const active = activeChip === c.key;
          const Icon = c.icon;
          const activeClass = ACCENT_ACTIVE[c.accent] || ACCENT_ACTIVE.slate;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => onChipChange && onChipChange(c.key)}
              className={`shrink-0 snap-start whitespace-nowrap inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm transition md:px-3 md:py-1.5 ${
                active
                  ? activeClass
                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
              data-testid={`${testIdPrefix}-filter-${c.key}`}
              aria-pressed={active}
            >
              {Icon && <Icon className="h-3.5 w-3.5" />}
              <span>{c.label}</span>
              {typeof c.count === 'number' && (
                <span className={`text-xs tabular-nums ${active ? 'opacity-90' : 'text-slate-500'}`}>
                  {c.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="relative mb-3">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => onQueryChange && onQueryChange(e.target.value)}
          placeholder={placeholder}
          className="pl-9 pr-9 h-11 text-base md:h-10 md:text-sm bg-white"
          data-testid={`${testIdPrefix}-search`}
          inputMode="search"
          autoComplete="off"
        />
        {query && (
          <button
            type="button"
            onClick={() => onQueryChange && onQueryChange('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1.5"
            aria-label="Clear search"
            data-testid={`${testIdPrefix}-search-clear`}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {showCount && resultCount !== null && totalCount !== null && (
        <div className="mb-3 text-sm text-slate-500" data-testid={`${testIdPrefix}-search-count`}>
          {resultCount === 0
            ? `No ${resultNoun}s match this filter.`
            : `${resultCount} of ${totalCount} ${resultNoun}${totalCount === 1 ? '' : 's'} match.`}
        </div>
      )}
    </>
  );
};

export default ListFilterBar;
